import React from 'react';
import { InlineContentEditor } from '@/components/admin/InlineContentEditor';

const defaultRulesContent = `<div class="space-y-8">
  <div class="space-y-4">
    <h1 class="text-4xl font-bold text-foreground">Minor Soccer Talks – Community Rules & Posting Guidelines</h1>
    <p class="text-lg text-muted-foreground">Welcome to Minor Soccer Talks – a forum created to foster respectful, insightful, and engaging conversations about youth soccer. These rules outline expectations for all members to ensure a positive and productive environment for players, parents, coaches, and fans.</p>
  </div>

  <div class="space-y-4">
    <h2 class="text-2xl font-semibold text-foreground flex items-center gap-2">
      🧾 General Community Guidelines
    </h2>
    <p class="text-muted-foreground">By using this forum, you agree to follow these rules, as well as our <a href="/terms" class="text-primary hover:underline">Terms of Use</a> and <a href="/privacy" class="text-primary hover:underline">Privacy Policy</a>. These rules apply to all members and guests, and exist to promote respectful dialogue and protect the community.</p>
  </div>

  <div class="space-y-6">
    <h2 class="text-2xl font-semibold text-foreground flex items-center gap-2">
      📌 Posting Guidelines
    </h2>
    <p class="text-muted-foreground">Before posting, please keep the following in mind:</p>

    <div class="space-y-4 pl-4 border-l-2 border-primary/20">
      <div class="space-y-2">
        <h3 class="text-xl font-semibold text-foreground">Use Constructive Language</h3>
        <p class="text-muted-foreground">Share your opinions using examples and insights.</p>
        <ul class="space-y-1 list-none">
          <li class="text-destructive">❌ "TM coach is an idiot for subbing the striker."</li>
          <li class="text-green-600">✅ "The striker was subbed off while we were pressing for a goal with 10 minutes left, which stalled our momentum. That decision was questionable."</li>
        </ul>
      </div>

      <div class="space-y-2">
        <h3 class="text-xl font-semibold text-foreground">Avoid Personal Attacks</h3>
        <p class="text-muted-foreground">Criticism of decisions is fine — personal attacks on individuals, parents, players, or businesses are not. Keep discussion respectful and on-topic.</p>
      </div>

      <div class="space-y-2">
        <h3 class="text-xl font-semibold text-foreground">No Repetitive Posts</h3>
        <p class="text-muted-foreground">Avoid posting the same opinion multiple times or repeating content already shared without adding value.</p>
      </div>

      <div class="space-y-2">
        <h3 class="text-xl font-semibold text-foreground">Don't Quote Problematic Posts</h3>
        <p class="text-muted-foreground">If you quote a post that violates the rules and it gets removed, your quote may also be removed. Stick to your own words.</p>
      </div>

      <div class="space-y-2">
        <h3 class="text-xl font-semibold text-foreground">Do Not Single Out Children</h3>
        <p class="text-muted-foreground">Avoid criticizing specific players by name or number.</p>
        <ul class="space-y-1 list-none">
          <li class="text-destructive">❌ "#14 on defense was terrible."</li>
          <li class="text-green-600">✅ "The defense had a tough time last night — several players were caught flat-footed."</li>
        </ul>
      </div>

      <div class="space-y-2">
        <h3 class="text-xl font-semibold text-foreground">Use Initials or General Descriptions When Possible</h3>
        <p class="text-muted-foreground">When referring to parents, coaches, or players, use initials or general terms unless the person is a public figure (e.g., professional soccer player, coach, or someone already in the public domain).</p>
      </div>
    </div>
  </div>

  <div class="space-y-4">
    <h2 class="text-2xl font-semibold text-foreground flex items-center gap-2">
      🔒 Moderation & Responsibility
    </h2>
    <p class="text-muted-foreground">Posts are reviewed for the greater good of the community, based on the rules above.</p>
    <ul class="space-y-2 list-disc list-inside text-muted-foreground">
      <li>You are responsible for everything you post.</li>
      <li>While guest posts may appear anonymous, your IP address and post data are logged for moderation and safety purposes.</li>
      <li>Moderators may remove, edit, or restrict posts that violate these rules without prior notice.</li>
    </ul>
  </div>

  <div class="space-y-4">
    <h2 class="text-2xl font-semibold text-foreground flex items-center gap-2">
      🚩 Reporting Posts
    </h2>
    <p class="text-muted-foreground">To report a post:</p>
    <ol class="space-y-2 list-decimal list-inside text-muted-foreground pl-4">
      <li>Click the flag icon below the post.</li>
      <li>The post will be immediately hidden from public view pending moderator review.</li>
      <li>Provide a brief explanation of the concern when prompted.</li>
    </ol>
    <div class="mt-4 space-y-2">
      <p class="font-semibold text-foreground">Please note:</p>
      <ul class="space-y-2 list-disc list-inside text-muted-foreground">
        <li>A flagged post is not automatically deleted.</li>
        <li>Just because you disagree with someone's opinion does not mean it violates the rules or will be permanently removed.</li>
        <li>All flags are reviewed within 24 hours.</li>
      </ul>
    </div>
  </div>

  <div class="space-y-4">
    <h2 class="text-2xl font-semibold text-foreground flex items-center gap-2">
      📬 Contact Us
    </h2>
    <p class="text-muted-foreground">Have a question or concern? Use the "Contact Us" link at the bottom of any page to reach out to the site administrators.</p>
  </div>
</div>`;

const ForumRules = () => {
  return (
    <InlineContentEditor
      settingKey="forum_rules_content"
      title="Forum Rules"
      defaultContent={defaultRulesContent}
    />
  );
};

export default ForumRules;